import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import {
  catchError,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  skip,
  takeUntil,
  tap,
  throwError,
  timer
} from 'rxjs';
import { environment } from '../../environments/environment';
import { Apis } from '../enums/apificado-api';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
  MenuCacheService,
  MENU_CACHE_KEY_PREFIX,
  FALLBACK_EXECUTED_KEY_PREFIX
} from './menu-cache.service';

/** Identidad mínima para armar claves `menuasteric::` (JWT o props del shell). */
export interface MenuCacheIdentidad {
  IdUsuario: string;
  IdEmpresa: number;
  IdOrganizacion: number;
}

/**
 * Clave de menú en localStorage sin depender de JWT.
 * `idPais` debe ser ISO alpha-2/3 si se incluye.
 */
export function construirMenuCacheKey(
  ident: MenuCacheIdentidad,
  idPais?: string | null
): string {
  let key = `${MENU_CACHE_KEY_PREFIX}${encodeURIComponent(ident.IdUsuario)}-${ident.IdEmpresa}-${ident.IdOrganizacion}`;
  if (idPais != null && typeof idPais === 'string') {
    const t = idPais.trim();
    if (/^[A-Za-z]{2,3}$/.test(t)) {
      key += `-${t.toUpperCase()}`;
    }
  }
  return key;
}
import { HelperService } from './helper.service';

/**
 * Respuesta de `api/v1/autenticacion/AutenticarNuevoProducto` (nuevo contrato).
 * El campo con el JWT de permisos es `tokenPermisos` (no `token`).
 */
export interface AutenticarNuevoProductoResponse {
  accessToken?: string | null;
  expiresIn?: number;
  refreshToken?: string | null;
  idToken?: string | null;
  tokenPermisos?: string | null;
  returnUrl?: string | null;
  session?: unknown;
  usaCooke?: boolean;
  logoutUrl?: string | null;
  retryLowerCase?: boolean;
  /** Compat: respuestas antiguas con `token` en lugar de `tokenPermisos` */
  token?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private static readonly TOKEN_POLL_INTERVAL_MS = 2_000;
  private static readonly TOKEN_POLL_MAX_MS = 30_000;

  /** Sesión SSO en curso para deduplicar llamadas paralelas a `ensureTokenPermisos$`. */
  private tokenPermisosInFlight$: Observable<AutenticarNuevoProductoResponse | null> | null = null;

  /**
   * Último contexto usado en `ensureTokenPermisos$` con `idAplicacion` y `filtroAdicional` no vacíos
   * (p. ej. menú / shell). Lo usa el interceptor para re-adquirir permisos sin depender del componente.
   */
  private ultimoContextoPermisos: {
    idAplicacion: string;
    filtroAdicional: string;
  } | null = null;

  constructor(
    private cookieServices: CookieService,
    private http: HttpClient,
    private jwtHelper: JwtHelperService,
    private menuCacheService: MenuCacheService,
    private helperService: HelperService
  ) { }

  public get currentPermission(): string | null {
    var tokenPermisos = this.cookieServices.get('tokenPermisos') || null;
    if(!tokenPermisos){
      return null;
    }
    return tokenPermisos;
  }

  /**
   * Emite cuando `tokenPermisos` cambia en cookie (polling 2 s, máx 20 s).
   * La emisión inicial se descarta para no duplicar la carga de `ngOnInit`.
   */
  public tokenPermisosCambios$(): Observable<string | null> {
    return timer(0, AuthService.TOKEN_POLL_INTERVAL_MS).pipe(
      map(() => this.currentPermission),
      distinctUntilChanged(),
      skip(1),
      takeUntil(timer(AuthService.TOKEN_POLL_MAX_MS))
    );
  }

  public get currentAccessToken(): string | null {
    var accessToken = this.cookieServices.get('accessToken') || null;
    return accessToken;
  }

  public get currentRefreshToken(): string {
    return this.cookieServices.get('refreshToken');
  }

  public get currentIdToken(): string {
    return this.cookieServices.get('idToken');
  }

  public get currentUserToken(): string {
    return this.cookieServices.get('currentUser');
  }

  private saveAccessToken(accessToken: string) {
    this.cookieServices.set('accessToken', accessToken, 0, '/', this.getDomainName(), true, 'Lax');
  }

  public saveTokenPermisos(tokenPermisos: string) {
    this.cookieServices.set('tokenPermisos', tokenPermisos, 0, '/', this.getDomainName(), true, 'Lax');
  }

  private setCookieSiHayValor(
    name: 'accessToken' | 'refreshToken' | 'idToken' | 'currentUser',
    value: string | null | undefined
  ): void {
    const v = value?.trim() ?? '';
    if (!v) {
      return;
    }
    this.cookieServices.set(name, v, 0, '/', this.getDomainName(), true, 'Lax');
  }

  /**
   * Persiste en cookies lo que venga con valor de la respuesta de AutenticarNuevoProducto.
   * Prioridad para permisos: `tokenPermisos` y, si no hay, `token` (legado).
   */
  public aplicarRespuestaAutenticarNuevoProducto(
    data: AutenticarNuevoProductoResponse | null | undefined
  ): void {
    if (!data) {
      return;
    }
    const permisos = data.tokenPermisos?.trim() || data.token?.trim() || '';
    if (permisos) {
      this.saveTokenPermisos(permisos);
    }
    const access = data.accessToken?.trim() ?? '';
    if (access) {
      this.saveAccessToken(access);
    }
    this.setCookieSiHayValor('refreshToken', data.refreshToken);
    this.setCookieSiHayValor('idToken', data.idToken);
  }

  refreshToken(): Observable<void> {
    return this.http.get<{ accessToken: string }>(`${environment.urlApiSSO}api/v1/autenticacion/refrescartoken?token=${this.getRefreshToken()}`)
      .pipe(map((data) => {
        if (data && data.accessToken !== '') {
          this.saveAccessToken(data.accessToken);
        } else {
          this.logout();
          throw new Error('No access token in response');
        }
      }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  logout() {
    this.cookieServices.delete('accessToken','/');
    this.cookieServices.delete('refreshToken','/');
    this.cookieServices.delete('idToken','/');
    this.cookieServices.delete('currentUser','/');
    this.cookieServices.delete('tokenPermisos','/');
    this.ultimoContextoPermisos = null;
    this.menuCacheService.limpiarTodas();
  }

  private getRefreshToken(): string {
    return this.cookieServices.get('refreshToken') || '';
  }

  private getDomainName(): string {
    let url = window.location.hostname;
    let host = url.substring(url.lastIndexOf(".", url.lastIndexOf(".") - 1) + 1);
    if (host === environment.domainCookie) {
      return environment.domainCookie; // iconstruye
    } else {
      return environment.domainCookie2; // Agilice
    }
  }

  /**
   * Cabeceras base; si hay `accessToken` en cookie, añade `Authorization: Bearer ...`.
   * (El interceptor no toca `AutenticarNuevoProducto`, el Bearer debe ir aquí si aplica.)
   */
  private buildHeadersConBearerSiHayAccessToken(): HttpHeaders {
    const base: Record<string, string> = {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache'
    };
    const access = this.currentAccessToken?.trim() ?? '';
    if (access) {
      base['Authorization'] = `Bearer ${access}`;
    }
    return new HttpHeaders(base);
  }

  public obtenerTokenPermisos(
    identificador: string,
    filtroAdicional: string
  ): Observable<AutenticarNuevoProductoResponse> {
    const filtro = encodeURIComponent(filtroAdicional);
    const url = `api/v1/autenticacion/AutenticarNuevoProducto?idAplicacion=${encodeURIComponent(
      identificador
    )}&filtroAdicional=${filtro}`;

    const httpOptions = {
      headers: this.buildHeadersConBearerSiHayAccessToken()
    };
    
    return this.http
      .get<AutenticarNuevoProductoResponse>(environment.urlApiSSO + url, httpOptions)
      .pipe(
        map((data) => data),
        catchError((error) => throwError(() => error))
      );
  }

  /**
   * Contexto para `AutenticarNuevoProducto` cuando el interceptor debe re-adquirir permisos.
   * Si no hubo aún una llamada con filtro no vacío, usa `environment.idAplicacion` y filtro vacío.
   */
  public getContextoPermisos(): { idAplicacion: string; filtroAdicional: string } {
    const fallback = {
      idAplicacion: environment.idAplicacion,
      filtroAdicional: ''
    };
    if (!this.ultimoContextoPermisos) {
      return fallback;
    }
    const id = this.ultimoContextoPermisos.idAplicacion?.trim() ?? '';
    const filtro = this.ultimoContextoPermisos.filtroAdicional ?? '';
    if (!id) {
      return fallback;
    }
    return { idAplicacion: id, filtroAdicional: filtro };
  }

  /**
   * Persiste el contexto usado para obtener permisos (solo si ambos valores son no vacíos tras trim en id).
   */
  private setContextoPermisosSiCompleto(idAplicacion: string, filtroAdicional: string): void {
    const id = idAplicacion?.trim() ?? '';
    const filtro = filtroAdicional ?? '';
    if (!id || !filtro.trim()) {
      return;
    }
    this.ultimoContextoPermisos = { idAplicacion: id, filtroAdicional: filtro };
  }

  /**
   * Invalida `tokenPermisos` en cookie y fuerza una nueva llamada a SSO con el último contexto conocido.
   * Útil ante 403 del BFF por token de permisos inválido o desalineado.
   */
  public forzarRenovacionTokenPermisos$(): Observable<AutenticarNuevoProductoResponse | null> {
    this.tokenPermisosInFlight$ = null;
    this.cookieServices.delete('tokenPermisos', '/');
    const ctx = this.getContextoPermisos();
    return this.ensureTokenPermisos$(ctx.idAplicacion, ctx.filtroAdicional);
  }

  /**
   * Garantiza que existan `tokenPermisos` y `accessToken` en cookie antes de llamadas al gateway.
   * Reutiliza una única petición en curso (`shareReplay`) para llamadas paralelas.
   */
  public ensureTokenPermisos$(
    idAplicacion: string,
    filtroAdicional: string
  ): Observable<AutenticarNuevoProductoResponse | null> {
    const perm = this.currentPermission?.trim() ?? '';
    const access = this.currentAccessToken?.trim() ?? '';
    const identidadValida = this.getIdentidadUsuario() != null;
    if (perm && access && identidadValida) {
      return of(null);
    }
    if (this.tokenPermisosInFlight$) {
      return this.tokenPermisosInFlight$;
    }
    this.setContextoPermisosSiCompleto(idAplicacion, filtroAdicional);
    this.tokenPermisosInFlight$ = this.obtenerTokenPermisos(idAplicacion, filtroAdicional).pipe(
      tap((data) => this.aplicarRespuestaAutenticarNuevoProducto(data)),
      shareReplay({ bufferSize: 1, refCount: false }),
      finalize(() => {
        this.tokenPermisosInFlight$ = null;
      })
    );
    return this.tokenPermisosInFlight$;
  }

  /**
   * Atajo: codifica identidad del shell y llama `AutenticarNuevoProducto`
   * (`idAplicacion` = `environment.idCambioEmpresa`).
   */
  public ensureTokenPermisosConIdentidad$(
    idEmpresa: number,
    idUsuario: string,
    idOrganizacion: number,
    dominio: string = ''
  ): Observable<AutenticarNuevoProductoResponse | null> {
    const filtroAdicional = this.helperService.enviarIdentificadorCodificado(
      idEmpresa,
      idUsuario,
      idOrganizacion,
      dominio
    );
    return this.ensureTokenPermisos$(environment.idCambioEmpresa, filtroAdicional);
  }

  /**
   * Identidad a partir de tokenPermisos: campo JWT `permisos` (base64 JSON).
   * Fail-closed: null si el token o permisos no son válidos.
   */
  public getIdentidadUsuario(): {
    IdUsuario: string;
    IdEmpresa: number;
    IdOrganizacion: number;
  } | null {
    const token = this.currentPermission;
    if (!token) {
      return null;
    }
    try {
      const decoded = this.jwtHelper.decodeToken(token) as { permisos?: string } | null;
      if (!decoded?.permisos) {
        return null;
      }
      const permisos = JSON.parse(atob(decoded.permisos)) as {
        IdUsuario?: string | number;
        IdEmpresa?: number;
        IdOrganizacion?: number;
      };
      if (permisos?.IdUsuario == null || permisos?.IdEmpresa == null || permisos?.IdOrganizacion == null) {
        return null;
      }
      return {
        IdUsuario: String(permisos.IdUsuario),
        IdEmpresa: Number(permisos.IdEmpresa),
        IdOrganizacion: Number(permisos.IdOrganizacion)
      };
    } catch {
      return null;
    }
  }

  /**
   * Nombre de empresa/organización desde el JWT `permisos` (mismo origen que asteric-oc-front).
   */
  public getEmpresaNombre(): string | null {
    const token = this.currentPermission;
    if (!token) {
      return null;
    }
    try {
      const decoded = this.jwtHelper.decodeToken(token) as { permisos?: string } | null;
      if (!decoded?.permisos) {
        return null;
      }
      const permisos = JSON.parse(atob(decoded.permisos)) as Record<string, unknown>;
      const nombre = permisos['NombreEmpresa']
        ?? permisos['Nombre Empresa']
        ?? permisos['empresa_nombre']
        ?? permisos['empresaNombre']
        ?? permisos['nombreEmpresa'];
      return typeof nombre === 'string' && nombre.trim() ? nombre.trim() : null;
    } catch {
      return null;
    }
  }

  /**
   * `idPais` debe ser un código alfanumérico corto (p. ej. CO); si no es válido se ignora.
   */
  public getMenuCacheKey(idPais?: string | null): string | null {
    const ident = this.getIdentidadUsuario();
    if (!ident) {
      return null;
    }
    return construirMenuCacheKey(ident, idPais);
  }

  /**
   * Clave base en `sessionStorage` para la política anti-loop del `Fallback()` del shell
   * (el componente añade `-registro` si aplica y persiste JSON `{ attempts, firstAttemptAt, lastAttemptAt }`):
   * `fallbackExecuted::<IdUsuario>-<IdEmpresa>-<IdOrganizacion>[-<IdPais>]`.
   */
  public getFallbackExecutedCacheKey(idPais?: string | null): string | null {
    const ident = this.getIdentidadUsuario();
    if (!ident) {
      return null;
    }
    let key = `${FALLBACK_EXECUTED_KEY_PREFIX}${encodeURIComponent(ident.IdUsuario)}-${ident.IdEmpresa}-${ident.IdOrganizacion}`;
    if (idPais != null && typeof idPais === 'string') {
      const t = idPais.trim();
      if (/^[A-Za-z]{2,3}$/.test(t)) {
        key += `-${t.toUpperCase()}`;
      }
    }
    return key;
  }

  public  obtenerTipoEmpresa(currentPermission:string): string | null {
    let enterpriseSelected: string | null = null;    
    if (currentPermission) {
      try {
        const decodedToken = this.jwtHelper.decodeToken(currentPermission);
        if (decodedToken && decodedToken.permisos) {
          const permisosDecodificados = JSON.parse(atob(decodedToken.permisos));
          enterpriseSelected = permisosDecodificados.TipoEmpresa?.toString() || null;
        }
      } catch (error) {
        console.error('Error al decodificar el token:', error);
        return null;
      }
    }
    
    return enterpriseSelected;
  }
}
